import { removePendingMessage } from '../store/state';
import type { OptimisticMessage, Attachment } from '../types';

interface MessageBubbleProps {
  message: OptimisticMessage;
}

/**
 * Format timestamp for display
 */
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // Less than a minute
  if (diff < 60000) {
    return 'Just now';
  }

  // Less than an hour
  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return `${mins}m ago`;
  }

  // Same day
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  // Older
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentItem({ attachment, isCustomer }: { attachment: Attachment; isCustomer: boolean }) {
  const isImage = attachment.file_type?.startsWith('image/');
  const url = attachment.download_url;

  if (!url) {
    return (
      <div class={`easyhelp-attachment ${isCustomer ? 'customer' : 'operator'}`}>
        <span class="easyhelp-attachment-name">{attachment.file_name}</span>
        <span class="easyhelp-attachment-size">{formatFileSize(attachment.file_size)}</span>
      </div>
    );
  }

  if (isImage) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" class="easyhelp-attachment-image">
        <img src={url} alt={attachment.file_name} loading="lazy" />
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" class={`easyhelp-attachment ${isCustomer ? 'customer' : 'operator'}`}>
      <span class="easyhelp-attachment-name">{attachment.file_name}</span>
      <span class="easyhelp-attachment-size">{formatFileSize(attachment.file_size)}</span>
    </a>
  );
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isSending = message._status === 'sending';
  const isFailed = message._status === 'failed';

  const handleRetry = () => {
    if (message._idempotencyKey) {
      // Remove failed message
      removePendingMessage(message._idempotencyKey);
      // Re-add as pending (the composer will handle sending)
      // Actually, we need to emit an event or callback here
      // For now, just remove the failed message
      // The user can re-type and send
    }
  };

  const isCustomer = message.sender_type === 'customer';

  return (
    <div
      class={`easyhelp-message ${message.sender_type} ${isSending ? 'sending' : ''} ${isFailed ? 'failed' : ''}`}
    >
      <div class="easyhelp-message-text">
        {message.sanitized_text}
      </div>

      {message.attachments && message.attachments.length > 0 && (
        <div class="easyhelp-attachments">
          {message.attachments.map((att: Attachment) => (
            <AttachmentItem key={att.id} attachment={att} isCustomer={isCustomer} />
          ))}
        </div>
      )}

      {!isSending && !isFailed && (
        <div class="easyhelp-message-time">
          {formatTime(message.created_at)}
        </div>
      )}

      {isSending && (
        <div class="easyhelp-message-time">
          Sending...
        </div>
      )}

      {isFailed && (
        <button class="easyhelp-message-retry" onClick={handleRetry}>
          Failed - Tap to remove
        </button>
      )}
    </div>
  );
}
